import { Button } from "@/src/components/ui/button";
import { Content } from "@/src/components/wrappers/Content";
import { Link, Outlet } from "react-router-dom";
import { ActionsContext, useEmit } from "../../lib/actions";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { ActionButton } from "../../components/wrappers/WithActionBar";
import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { FiltersForm } from "./FiltersForm/FiltersForm";
import { useOffersStore } from "../../stores/offersStore";
import { useUserStore } from "../../stores/userStore";
import { PlusIcon } from "lucide-react";

// The logged-in id is a HubSpot owner id; offers/bookings reference the database
// user id, so match on that (falling back keeps pre-fix sessions no worse off).
function brokerDbId(u: any): number | undefined {
  const v = u?.databaseId ?? u?.id;
  return v == null || Number.isNaN(+v) ? undefined : +v;
}

export function Offers() {
  const [visible, setVisible] = useState(false);
  const { filters } = useOffersStore();

  return (
    <ActionsContext>
      <Content
        title="Offers"
        contentAction={
          <Button asChild>
            <Link to="/offers/add">
              Add an Offer
              <PlusIcon className="ml-2" size={16} />
            </Link>
          </Button>
        }
      >
        <div className="mb-2 flex flex-col gap-2">
          <div className="flex justify-between">
            <div className="flex gap-4">
              <Dialog onOpenChange={(v) => setVisible(v)} open={visible}>
                <DialogTrigger asChild>
                  <Button className="bg-white" variant="outline">
                    Filters
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[720px] max-h-[calc(100%-80px)] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-3xl">Filters</DialogTitle>
                  </DialogHeader>
                  <FiltersForm onSave={() => setVisible(false)} />
                  <DialogFooter>
                    <ActionButton
                      disabled={(!!filters && !Object.keys(filters)?.length) || !filters}
                      variant="link"
                      className="mr-auto"
                      eventKey="filters:clear"
                    >
                      Clear all
                    </ActionButton>
                    <ActionButton eventKey="filters:save" type="submit">
                      Show offers
                    </ActionButton>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Button onClick={() => alert("FEATURE IN DEV")} className="bg-white hover:bg-white" variant="outline">
                Export
              </Button>
            </div>
            <div className="flex gap-x-4">
              <MyOffsersCheckbox />
              <SortingSelect />
            </div>
          </div>
          <Outlet />
        </div>
      </Content>
    </ActionsContext>
  );
}

function MyOffsersCheckbox() {
  const { filters, setFilters } = useOffersStore();
  const { user } = useUserStore();
  const [checked, setChecked] = useState(filters?.["broker"]?.id === brokerDbId(user));

  useEffect(() => {
    setChecked(filters?.["broker"]?.id === brokerDbId(user));
  }, [filters]);

  const handleClick = () => {
    const truncatedFilters = { ...filters };
    delete truncatedFilters["broker"];
    setChecked(!checked);
    return !checked
      ? setFilters({
          ...filters,
          broker: {
            id: brokerDbId(user)!,
            firstName: user?.firstName!,
            lastName: user?.lastName!,
            fullName: user?.firstName! + " " + user?.lastName!,
          },
        })
      : setFilters(truncatedFilters);
  };

  return (
    <div className="flex rounded-md border border-slate-200 overflow-hidden">
      <button
        type="button"
        onClick={() => (checked ? undefined : handleClick())}
        className={`px-4 py-2 text-sm font-medium ${checked ? "bg-blue-600 text-white" : "bg-white text-slate-700"}`}
      >
        My offers
      </button>
      <button
        type="button"
        onClick={() => (checked ? handleClick() : undefined)}
        className={`px-4 py-2 text-sm font-medium ${!checked ? "bg-blue-600 text-white" : "bg-white text-slate-700"}`}
      >
        All brokers
      </button>
    </div>
  );
}

function SortingSelect() {
  const [value, setValue] = useState("DESC");
  const emit = useEmit<string>("ych:sorting");

  const onValueChange = (value: string) => {
    setValue(value);
    emit(value);
  };

  const options: { key: string; label: string }[] = [
    { key: "DESC", label: "Recently updated" },
    { key: "recently_seen", label: "Recently seen" },
    { key: "recent_feedback", label: "Recent feedback" },
    { key: "on_option", label: "On option" },
  ];
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-sm text-slate-500 mr-1">Sort:</span>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onValueChange(o.key)}
          className={`text-sm rounded-md px-3 py-1.5 font-medium ${
            value === o.key ? "bg-blue-600 text-white" : "bg-white text-slate-700 border border-slate-200"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
